import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, Check, RotateCcw } from 'lucide-react';

import { avisarSmackCambiado } from '../../lib/smackBus';
import useSmackPolling from './useSmackPolling';

const ISSUES_POLL_MS = 15000; // mismo régimen que la lista de canales de SmackPage

function formatearFecha(iso) {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return '';
  const hoy = new Date();
  const esHoy = fecha.toDateString() === hoy.toDateString();
  if (esHoy) return fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return fecha.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

/**
 * Backlog de mensajes marcados como pendiente (versión ligera del F3 del plan
 * 21-ago, sin triaje IA). Lista aparte del scroll del chat a propósito: un
 * problema no debe depender de que alguien se acuerde de bajar por el canal
 * para encontrarlo. Al resolver, el mensaje sale de la vista de abiertos pero
 * no se borra — reabrir es marcar de nuevo.
 */
export default function PendingIssuesPanel({ token, onOpenChannel, onBack, isSuperadmin }) {
  const [verResueltos, setVerResueltos] = useState(false);
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [actualizando, setActualizando] = useState(null); // id en curso

  const cargar = useCallback(() => {
    if (!token) return;
    axios
      .get('/v1/smack/issues', {
        headers: { Authorization: `Bearer ${token}` },
        params: { resolved: verResueltos },
      })
      .then((r) => {
        setItems(Array.isArray(r.data?.items) ? r.data.items : []);
        setError('');
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || 'No se pudieron cargar los pendientes.');
      })
      .finally(() => setCargando(false));
  }, [token, verResueltos]);

  // `useSmackPolling` ya dispara un tick inmediato al montar. Este efecto es
  // solo para el cambio de pestaña (abiertos ↔ resueltos), que no re-arranca el
  // sondeo: sin saltarse la primera vuelta, abrir el panel pedía /issues dos
  // veces seguidas.
  const primeraVuelta = useRef(true);
  useEffect(() => {
    if (primeraVuelta.current) {
      primeraVuelta.current = false;
      return;
    }
    setCargando(true);
    cargar();
  }, [cargar]);

  useSmackPolling(cargar, ISSUES_POLL_MS, Boolean(token));

  const alternarResuelto = useCallback(
    async (issue) => {
      setActualizando(issue.id);
      try {
        await axios.patch(
          `/v1/smack/issues/${issue.id}`,
          { resolved: !issue.resolved },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setItems((prev) => prev.filter((i) => i.id !== issue.id)); // ya no encaja en el filtro actual
        avisarSmackCambiado(); // refresca el badge del sidebar ya, sin esperar el sondeo
      } catch (err) {
        setError(err?.response?.data?.detail || 'No se pudo actualizar el pendiente.');
      } finally {
        setActualizando(null);
      }
    },
    [token]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 md:hidden"
            aria-label="Volver a canales"
          >
            <ArrowLeft size={18} />
          </button>
          <h2 className="text-sm font-semibold text-gray-900">Pendientes</h2>
        </div>
        <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setVerResueltos(false)}
            className={`rounded-md px-2 py-1 font-medium ${
              !verResueltos ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
            }`}
          >
            Abiertos
          </button>
          <button
            type="button"
            onClick={() => setVerResueltos(true)}
            className={`rounded-md px-2 py-1 font-medium ${
              verResueltos ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
            }`}
          >
            Resueltos
          </button>
        </div>
      </div>

      {error ? <p className="border-b border-red-100 bg-red-50 px-4 py-1.5 text-xs text-red-700">{error}</p> : null}

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {cargando ? <p className="px-3 py-2 text-sm text-gray-400">Cargando…</p> : null}
        {!cargando && items.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-gray-400">
            {verResueltos ? 'Nada resuelto todavía.' : 'Sin pendientes abiertos.'}
          </p>
        ) : null}

        {items.map((issue) => (
          <div
            key={issue.id}
            className="mb-1 flex items-start gap-2 rounded-lg border border-gray-200 p-2.5 hover:bg-gray-50"
          >
            <button
              type="button"
              onClick={() => onOpenChannel?.(issue.channel_id)}
              className="min-w-0 flex-1 text-left"
            >
              <p className="flex items-center gap-1.5 text-[11px] text-gray-400">
                <span className="text-gray-500"># {issue.channel_name}</span>
                {issue.venue_name ? <span>· {issue.venue_name}</span> : null}
                <span>· {formatearFecha(issue.flagged_at)}</span>
              </p>
              <p className="truncate text-sm text-gray-800">{issue.message_preview || '(sin texto)'}</p>
              <p className="text-[11px] text-gray-400">
                {issue.resolved
                  ? `Resuelto por ${issue.resolved_by_name || '—'}`
                  : `Marcado por ${issue.flagged_by_name || '—'}`}
              </p>
            </button>
            {/* Resolver es del equipo: el club marca y consulta, pero no cierra.
                Lo manda el API (403); esto solo evita ofrecer un botón muerto.
                #FF5400: acento de marca de Smack (decisión 22-ago: se queda) — fuera
                de tokens.css a propósito: el design system es slate y este naranja es
                exclusivo del módulo. */}
            {isSuperadmin ? (
              <button
                type="button"
                onClick={() => alternarResuelto(issue)}
                disabled={actualizando === issue.id}
                className="mt-0.5 flex shrink-0 items-center gap-1 rounded-lg bg-[#FF5400] px-2.5 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {issue.resolved ? <RotateCcw size={13} /> : <Check size={13} />}
                {issue.resolved ? 'Reabrir' : 'Resuelto'}
              </button>
            ) : (
              <span className="mt-1 shrink-0 text-[11px] text-gray-400">
                {issue.resolved ? 'Resuelto' : 'Abierto'}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
